import type { Device } from '@twilio/voice-sdk'

/**
 * 2e singleton Device Twilio dédié au Power Dialer (subaccount).
 *
 * Pourquoi 2 Devices ? Le compte master Twilio (Voice token classique) ne peut
 * pas faire de Dial avec un caller_id qui appartient à un subaccount.
 * Les numéros Power Dialer vivent dans le subaccount de chaque company
 * (`twilio_subaccount_sid`), donc on a besoin d'un Device séparé enregistré
 * sur ce subaccount.
 *
 * Lifecycle :
 *  - `ensurePdDevice(token)` au moment où l'agent entre en session Power Dialer
 *  - `destroyPdDevice()` à la sortie (ou logout)
 *
 * Les events incoming sont aussi attachés sur ce Device pour recevoir les
 * rappels de prospects qui appellent un numéro PD (l'IVR Flow du subaccount
 * fait `<Client agent_X_Y>` → push uniquement sur le Device du même
 * subaccount, d'où l'isolation).
 */

let pdDeviceInstance: Device | null = null
let isInitializing = false
let pdTokenRefreshCallback: (() => Promise<void>) | null = null

const isDev = import.meta.env.DEV
const devLog = (...args: unknown[]) => {
  if (isDev) console.log(...args)
}
const devWarn = (...args: unknown[]) => {
  if (isDev) console.warn(...args)
}
const devError = (...args: unknown[]) => {
  if (isDev) console.error(...args)
}

export function getPdDevice(): Device | null {
  return pdDeviceInstance
}

export async function ensurePdDevice(token: string): Promise<Device> {
  if (pdDeviceInstance) {
    devLog('♻️ Réutilisation du PD Device singleton existant')
    return pdDeviceInstance
  }

  if (isInitializing) {
    devLog('⏳ Init PD Device en cours, attente…')
    while (isInitializing && !pdDeviceInstance) {
      await new Promise((resolve) => setTimeout(resolve, 100))
    }
    return pdDeviceInstance!
  }

  devLog('🔄 Création PD Device singleton (subaccount)')
  isInitializing = true

  try {
    const { Device: DeviceCtor } = await import('@twilio/voice-sdk')

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pdDeviceInstance = new DeviceCtor(token, {
      // Cf. twilioDeviceSingleton — 'debug' en dev, 'warn' en prod.
      logLevel: (isDev ? 'debug' : 'warn') as any,
      codecPreferences: ['opus', 'pcmu'] as any,
      closeProtection: false, // pas de prompt "vous avez un appel en cours" — le main Device le gère
    } as any)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pdDeviceInstance.on('error', (error: any) => {
      if (error.message?.includes('Insights') || error.message?.includes('eventgw')) {
        devWarn('⚠️ PD Insights non disponible (non critique):', error.message)
      } else if (error.code === 31401 || error.message?.includes('31401')) {
        devError('❌ PD micro permission refusée (31401)')
      } else {
        devError('❌ Erreur PD Device:', error)
      }
    })

    pdDeviceInstance.on('offline', () => {
      devWarn('⚠️ PD Device offline')
    })

    pdDeviceInstance.on('tokenWillExpire', async () => {
      devWarn('⚠️ PD token va expirer, refresh…')
      if (pdTokenRefreshCallback) {
        try {
          await pdTokenRefreshCallback()
          devLog('✅ PD token rafraîchi')
        } catch (error) {
          devError('❌ Refresh PD token échec:', error)
        }
      }
    })

    devLog('✅ PD Device singleton créé')
    return pdDeviceInstance
  } finally {
    isInitializing = false
  }
}

export function destroyPdDevice(): void {
  if (!pdDeviceInstance) return
  devLog('🧹 Destruction PD Device singleton')
  try {
    pdDeviceInstance.disconnectAll()
    pdDeviceInstance.removeAllListeners()
    pdDeviceInstance.unregister()
    pdDeviceInstance.destroy()
    devLog('✅ PD Device singleton détruit')
  } catch (error) {
    devWarn('⚠️ Erreur destruction PD Device:', error)
  }
  pdDeviceInstance = null
  isInitializing = false
}

export function isPdDeviceReady(): boolean {
  return pdDeviceInstance?.state === 'registered'
}

export function setPdTokenRefreshCallback(
  callback: (() => Promise<void>) | null,
): void {
  pdTokenRefreshCallback = callback
}
